<script setup lang="ts">
import { useBookingRepository, type PublicBookingProfile } from '~/repositories/bookingRepository'
import { useMarketplaceRepository } from '~/repositories/marketplaceRepository'
import type { PaymentMethodType, PublicPaymentMethod } from '~/repositories/paymentMethodRepository'

definePageMeta({ layout: 'public', middleware: ['tenant'] })

const route = useRoute()
const slug = computed(() => String(route.params.slug))
const repo = useBookingRepository()
const marketRepo = useMarketplaceRepository()
const toast = useToast()
const tenant = useTenant()

const profile = ref<PublicBookingProfile | null>(null)
const error = ref<string | null>(null)
const availableMethods = ref<PublicPaymentMethod[]>([])

try {
  const res = await repo.publicShowStaff(slug.value)
  profile.value = res.data
} catch {
  error.value = 'Booking page not found.'
}

if (profile.value && profile.value.main_price > 0) {
  availableMethods.value = await marketRepo.getTenantPaymentMethods()
}

// ── Slot picker ──────────────────────────────────────────────────────
const weekStart = ref<Date>(startOfWeek(new Date()))

function startOfWeek(d: Date): Date {
  const out = new Date(d)
  out.setHours(0, 0, 0, 0)
  out.setDate(out.getDate() - out.getDay())
  return out
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const weekDays = computed(() => {
  const days: Date[] = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart.value)
    d.setDate(d.getDate() + i)
    days.push(d)
  }
  return days
})

const slotRange = computed(() => ({
  from: toIsoDate(weekDays.value[0]),
  to: toIsoDate(weekDays.value[6]),
}))

const { data: slotsData, pending: slotsPending, refresh: refreshSlots } = await useAsyncData(
  () => `slots-${slug.value}-${slotRange.value.from}`,
  async () => {
    if (!profile.value) return { data: {}, timezone: '' }
    return await repo.publicSlots(slug.value, slotRange.value.from, slotRange.value.to)
  },
  { watch: [slotRange], default: () => ({ data: {} as Record<string, Array<{ starts_at: string; ends_at: string }>>, timezone: '' }) },
)

const slotsByDate = computed(() => slotsData.value?.data ?? {})

const selectedSlot = ref<string | null>(null)

const goPrevWeek = () => {
  const d = new Date(weekStart.value)
  d.setDate(d.getDate() - 7)
  weekStart.value = d
}
const goNextWeek = () => {
  const d = new Date(weekStart.value)
  d.setDate(d.getDate() + 7)
  weekStart.value = d
}

const formatDayHeader = (d: Date) => d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })
const formatSlotTime = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
const isToday = (d: Date) => toIsoDate(d) === toIsoDate(new Date())

// ── Booking modal ────────────────────────────────────────────────────
const showModal = ref(false)
const form = reactive({
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  customer_note: '',
})
const selectedMethod = ref<PaymentMethodType | null>(null)
const submitting = ref(false)

type BookingResponse = {
  data?: { id: number; starts_at: string; duration: number; status: string }
  status?: string
  booking_id?: number
  checkout_url?: string
  payment?: {
    id: number
    method: PaymentMethodType
    amount: number
    currency: string
    instructions?: Record<string, string> | null
    reference?: string
    qr_image?: string
    qpay_shorturl?: string
    deeplinks?: Array<{ name: string; link: string }>
    checkout_url?: string
    error?: string
  }
}
const result = ref<BookingResponse | null>(null)

const methodMeta: Record<PaymentMethodType, { label: string; icon: string }> = {
  bank_transfer: { label: 'Bank transfer', icon: 'i-lucide-building-2' },
  qpay: { label: 'QPay', icon: 'i-lucide-qr-code' },
  stripe: { label: 'Card', icon: 'i-lucide-credit-card' },
}

const distinctMethods = computed(() => {
  const seen = new Set<PaymentMethodType>()
  const out: PaymentMethodType[] = []
  for (const m of availableMethods.value) {
    if (!seen.has(m.method_type)) {
      seen.add(m.method_type)
      out.push(m.method_type)
    }
  }
  return out
})

const isPaid = computed(() => (profile.value?.main_price ?? 0) > 0)

const openBooking = (iso: string) => {
  selectedSlot.value = iso
  result.value = null
  selectedMethod.value = distinctMethods.value[0] ?? null
  showModal.value = true
}

const submitBooking = async () => {
  if (!selectedSlot.value || !profile.value) return
  if (isPaid.value && !selectedMethod.value) {
    toast.add({ title: 'Pick a payment method', color: 'warning' })
    return
  }
  submitting.value = true
  try {
    const res = await repo.publicBook(slug.value, {
      starts_at: selectedSlot.value,
      customer_name: form.customer_name,
      customer_email: form.customer_email,
      customer_phone: form.customer_phone || null,
      customer_note: form.customer_note || null,
      payment_method: isPaid.value ? selectedMethod.value : null,
    })

    if (res.checkout_url) {
      window.location.href = res.checkout_url
      return
    }

    result.value = res as BookingResponse
    await refreshSlots()
  } catch (e: unknown) {
    const err = e as { data?: { message?: string } }
    toast.add({ title: err.data?.message ?? 'Booking failed', color: 'error' })
  } finally {
    submitting.value = false
  }
}

const closeAndReset = () => {
  showModal.value = false
  form.customer_name = ''
  form.customer_email = ''
  form.customer_phone = ''
  form.customer_note = ''
  selectedSlot.value = null
  result.value = null
}

const bankDetails = computed(() => {
  const m = availableMethods.value.find(x => x.method_type === 'bank_transfer')
  return m?.details ?? {}
})

const copy = async (value: string | undefined) => {
  if (!value) return
  try {
    await navigator.clipboard.writeText(value)
    toast.add({ title: 'Copied', color: 'success' })
  } catch {
    toast.add({ title: 'Copy failed', color: 'error' })
  }
}

const downloadIcs = () => {
  const id = result.value?.data?.id ?? result.value?.booking_id
  if (!id) return
  // Must be the API origin, not the current page's origin — on a tenant
  // subdomain that's the Nuxt frontend server, which has no /api routes.
  const apiBase = useRuntimeConfig().public.apiBaseUrl
  window.open(`${apiBase}/api/public/bookings/${id}/ics`, '_blank')
}

// ── Receipt upload (bank transfer) ───────────────────────────────────
const receiptFile = ref<File | null>(null)
const receiptPreview = ref<string | null>(null)
const uploadingReceipt = ref(false)
const receiptUploaded = ref(false)

const onReceiptChange = (e: Event) => {
  const f = (e.target as HTMLInputElement).files?.[0] ?? null
  receiptFile.value = f
  receiptPreview.value = null
  if (f && f.type.startsWith('image/')) {
    const reader = new FileReader()
    reader.onload = ev => { receiptPreview.value = ev.target?.result as string }
    reader.readAsDataURL(f)
  }
}

const uploadReceipt = async () => {
  const id = result.value?.booking_id ?? result.value?.data?.id
  if (!id || !receiptFile.value) return
  uploadingReceipt.value = true
  try {
    await repo.publicUploadReceipt(id, form.customer_email, receiptFile.value)
    receiptUploaded.value = true
    toast.add({ title: 'Receipt uploaded', color: 'success' })
  } catch (e: unknown) {
    const err = e as { data?: { message?: string } }
    toast.add({ title: err.data?.message ?? 'Upload failed', color: 'error' })
  } finally {
    uploadingReceipt.value = false
  }
}

const priceLabel = computed(() => {
  if (!profile.value) return ''
  if (!profile.value.main_price) return 'Free'
  return `${profile.value.main_price} ${profile.value.currency ?? ''}`.trim()
})

useSeoMeta({
  title: () => profile.value ? `Book ${profile.value.display_name}${tenant.tenant.value?.name ? ` · ${tenant.tenant.value.name}` : ''}` : 'Book',
})
</script>

<template>
  <section class="mx-auto max-w-4xl px-6 py-10">
    <div v-if="error" class="rounded-lg bg-red-500/10 p-4 text-sm text-red-500">
      {{ error }}
    </div>

    <template v-else-if="profile">
      <NuxtLink to="/" class="inline-flex items-center gap-1 text-sm ui-text-subtle hover:underline">
        <UIcon name="i-lucide-arrow-left" class="size-4" /> Back
      </NuxtLink>

      <header class="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start">
        <img
          v-if="profile.avatar"
          :src="profile.avatar"
          :alt="profile.display_name"
          class="size-20 rounded-full object-cover"
        >
        <div v-else class="flex size-20 items-center justify-center rounded-full border ui-border-base ui-bg-base ui-text-subtle">
          <UIcon name="i-lucide-user" class="size-10" />
        </div>
        <div class="flex-1">
          <h1 class="text-2xl font-bold">{{ profile.display_name }}</h1>
          <p v-if="profile.title" class="text-base ui-text-subtle">{{ profile.title }}</p>
          <div class="mt-3 flex flex-wrap items-center gap-3 text-sm">
            <UBadge variant="soft" color="primary">{{ profile.session_duration }} min session</UBadge>
            <UBadge variant="soft" color="success">{{ priceLabel }}</UBadge>
            <span v-if="profile.rating_avg !== null" class="flex items-center gap-1 ui-text-subtle">
              <UIcon name="i-lucide-star" class="size-3.5 text-warning" />
              {{ profile.rating_avg.toFixed(1) }} <span class="opacity-60">· {{ profile.ratings_count }} reviews</span>
            </span>
            <span class="ui-text-subtle">Times in {{ profile.timezone }}</span>
          </div>
        </div>
      </header>

      <p v-if="profile.bio" class="mt-6 whitespace-pre-line text-base ui-text-subtle leading-relaxed">
        {{ profile.bio }}
      </p>

      <div v-if="profile.expertise?.length" class="mt-4 flex flex-wrap gap-1.5">
        <span
          v-for="tag in profile.expertise"
          :key="tag"
          class="rounded-full border ui-border-base ui-bg-base px-2.5 py-0.5 text-xs ui-text-subtle"
        >
          {{ tag }}
        </span>
      </div>

      <div class="mt-10 flex items-center justify-between">
        <h2 class="text-lg font-semibold ui-text-strong">Choose a time</h2>
        <div class="flex items-center gap-2">
          <UButton size="xs" variant="outline" color="neutral" icon="i-lucide-chevron-left" @click="goPrevWeek" />
          <span class="min-w-[6.5rem] text-center text-sm font-medium ui-text-strong">
            {{ weekDays[0].toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) }}
            –
            {{ weekDays[6].toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) }}
          </span>
          <UButton size="xs" variant="outline" color="neutral" icon="i-lucide-chevron-right" @click="goNextWeek" />
        </div>
      </div>

      <div class="mt-4 overflow-x-auto rounded-xl border ui-border-base ui-bg-base p-4">
        <div v-if="slotsPending" class="flex items-center gap-2 py-10 text-sm ui-text-subtle">
          <UIcon name="i-lucide-loader-circle" class="size-4 animate-spin" /> Loading slots…
        </div>
        <div v-else class="grid grid-cols-7 gap-3 min-w-[640px]">
          <div v-for="day in weekDays" :key="day.toISOString()" class="space-y-2">
            <div
              class="rounded-md py-1.5 text-center text-xs font-semibold ui-text-strong"
              :class="isToday(day) ? 'bg-primary/10 text-primary' : ''"
            >
              {{ formatDayHeader(day) }}
            </div>
            <div v-if="slotsByDate[toIsoDate(day)]?.length" class="space-y-1.5">
              <button
                v-for="slot in slotsByDate[toIsoDate(day)]"
                :key="slot.starts_at"
                type="button"
                class="block w-full rounded-md border ui-border-base bg-transparent px-2 py-1.5 text-center text-xs font-medium ui-text-strong transition-colors hover:border-primary hover:bg-primary/10 hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                @click="openBooking(slot.starts_at)"
              >
                {{ formatSlotTime(slot.starts_at) }}
              </button>
            </div>
            <div v-else class="py-2 text-center text-xs ui-text-subtle opacity-70">—</div>
          </div>
        </div>
      </div>

      <!-- Booking modal -->
      <UModal v-model:open="showModal" :ui="{ content: 'max-w-lg' }">
        <template #content>
          <div class="p-6 space-y-4">
            <!-- Success states -->
            <template v-if="result">
              <!-- Bank transfer instructions -->
              <template v-if="result.payment?.method === 'bank_transfer'">
                <div class="flex items-center gap-2">
                  <UIcon name="i-lucide-building-2" class="size-6 text-primary" />
                  <h3 class="text-lg font-semibold">Complete your bank transfer</h3>
                </div>
                <UAlert color="warning" variant="soft" icon="i-lucide-info">
                  Your slot is held. The booking will be confirmed once we receive your transfer.
                </UAlert>
                <div class="space-y-2 rounded-lg border border-default/60 p-4 text-sm">
                  <div v-if="result.payment.instructions?.bank_name" class="flex justify-between gap-3">
                    <span class="ui-text-subtle">Bank</span>
                    <span class="font-medium">{{ result.payment.instructions.bank_name }}</span>
                  </div>
                  <div v-if="result.payment.instructions?.account_name" class="flex justify-between gap-3">
                    <span class="ui-text-subtle">Account name</span>
                    <span class="font-medium">{{ result.payment.instructions.account_name }}</span>
                  </div>
                  <div v-if="result.payment.instructions?.account_number" class="flex items-center justify-between gap-3">
                    <span class="ui-text-subtle">Account number</span>
                    <span class="flex items-center gap-1 font-mono font-semibold">
                      {{ result.payment.instructions.account_number }}
                      <UButton size="xs" variant="ghost" icon="i-lucide-copy" @click="copy(result.payment.instructions.account_number)" />
                    </span>
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span class="ui-text-subtle">Amount</span>
                    <span class="font-semibold text-success">{{ result.payment.amount }} {{ result.payment.currency }}</span>
                  </div>
                  <div v-if="result.payment.reference" class="flex items-center justify-between gap-3 border-t border-default/40 pt-2">
                    <span class="ui-text-subtle">Reference</span>
                    <span class="flex items-center gap-1 font-mono font-semibold">
                      {{ result.payment.reference }}
                      <UButton size="xs" variant="ghost" icon="i-lucide-copy" @click="copy(result.payment.reference)" />
                    </span>
                  </div>
                </div>
                <!-- Receipt upload -->
                <div class="border-t border-default/40 pt-3">
                  <p class="mb-2 text-xs font-medium uppercase tracking-wide ui-text-subtle">Upload payment receipt (optional)</p>
                  <div v-if="receiptUploaded" class="flex items-center gap-2 text-sm text-success">
                    <UIcon name="i-lucide-check-circle-2" class="size-4" />
                    Receipt uploaded — we'll review it shortly.
                  </div>
                  <template v-else>
                    <label
                      v-if="!receiptFile"
                      class="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-default/60 px-4 py-3 text-sm hover:border-primary/50"
                    >
                      <UIcon name="i-lucide-image-plus" class="size-5 ui-text-subtle" />
                      <span class="ui-text-subtle">Attach screenshot or PDF</span>
                      <input type="file" class="hidden" accept="image/*,.pdf" @change="onReceiptChange">
                    </label>
                    <div v-else class="space-y-2">
                      <div class="flex items-center justify-between rounded-lg border border-default/60 px-3 py-2 text-sm">
                        <span class="flex items-center gap-2 truncate">
                          <UIcon name="i-lucide-paperclip" class="size-4 shrink-0" />
                          <span class="truncate">{{ receiptFile.name }}</span>
                        </span>
                        <UButton size="xs" variant="ghost" icon="i-lucide-x" @click="receiptFile = null; receiptPreview = null" />
                      </div>
                      <img v-if="receiptPreview" :src="receiptPreview" alt="Receipt" class="max-h-40 rounded-lg border border-default/60">
                      <UButton color="primary" size="sm" :loading="uploadingReceipt" @click="uploadReceipt">Upload receipt</UButton>
                    </div>
                  </template>
                </div>

                <div class="flex justify-end gap-2">
                  <UButton variant="outline" icon="i-lucide-download" @click="downloadIcs">Add to calendar</UButton>
                  <UButton color="primary" @click="closeAndReset">Done</UButton>
                </div>
              </template>

              <!-- QPay -->
              <template v-else-if="result.payment?.method === 'qpay'">
                <div class="flex items-center gap-2">
                  <UIcon name="i-lucide-qr-code" class="size-6 text-primary" />
                  <h3 class="text-lg font-semibold">Scan to pay</h3>
                </div>
                <div v-if="result.payment.qr_image" class="flex justify-center">
                  <img
                    :src="`data:image/png;base64,${result.payment.qr_image}`"
                    alt="QPay QR"
                    class="size-64"
                  >
                </div>
                <p class="text-center text-sm ui-text-subtle">
                  Scan with your banking app to pay {{ result.payment.amount }} {{ result.payment.currency }}.
                </p>
                <a
                  v-if="result.payment.qpay_shorturl"
                  :href="result.payment.qpay_shorturl"
                  target="_blank"
                  class="block text-center text-sm text-primary hover:underline"
                >Open in QPay app</a>
                <div class="flex justify-end gap-2">
                  <UButton variant="outline" icon="i-lucide-download" @click="downloadIcs">Add to calendar</UButton>
                  <UButton color="primary" @click="closeAndReset">Done</UButton>
                </div>
              </template>

              <!-- Free booking confirmation -->
              <template v-else>
                <div class="flex flex-col items-center gap-3 text-center py-2">
                  <UIcon name="i-lucide-check-circle-2" class="size-12 text-green-500" />
                  <h3 class="text-lg font-semibold">Booking confirmed</h3>
                  <p class="text-sm ui-text-subtle">We've sent a confirmation. {{ profile.display_name }} will be in touch.</p>
                </div>
                <div class="flex justify-end gap-2">
                  <UButton variant="outline" icon="i-lucide-download" @click="downloadIcs">Add to calendar</UButton>
                  <UButton color="primary" @click="closeAndReset">Done</UButton>
                </div>
              </template>
            </template>

            <!-- Form -->
            <template v-else>
              <h3 class="text-lg font-semibold">Confirm your booking</h3>
              <p v-if="selectedSlot" class="text-sm ui-text-subtle">
                {{ new Date(selectedSlot).toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'short' }) }}
              </p>

              <div class="space-y-3">
                <UFormField label="Name" required>
                  <UInput v-model="form.customer_name" placeholder="Your name" class="w-full" />
                </UFormField>
                <UFormField label="Email" required>
                  <UInput v-model="form.customer_email" type="email" placeholder="you@example.com" class="w-full" />
                </UFormField>
                <UFormField label="Phone (optional)">
                  <UInput v-model="form.customer_phone" placeholder="+61 …" class="w-full" />
                </UFormField>
                <UFormField label="Note (optional)">
                  <UTextarea v-model="form.customer_note" :rows="3" placeholder="What would you like to discuss?" class="w-full" />
                </UFormField>
              </div>

              <template v-if="isPaid">
                <p class="mt-2 text-xs font-medium uppercase tracking-wide ui-text-subtle">Payment method</p>
                <div v-if="distinctMethods.length" class="flex gap-2">
                  <button
                    v-for="m in distinctMethods"
                    :key="m"
                    class="flex flex-1 flex-col items-center gap-1.5 rounded-lg border px-3 py-3 text-xs font-medium transition-colors"
                    :class="selectedMethod === m
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-default/70 ui-text-subtle hover:border-primary/40'"
                    @click="selectedMethod = m"
                  >
                    <UIcon :name="methodMeta[m].icon" class="size-5" />
                    {{ methodMeta[m].label }}
                  </button>
                </div>
                <UAlert v-else color="warning" variant="soft" icon="i-lucide-alert-triangle">
                  No payment methods configured for this tenant yet.
                </UAlert>

                <!-- Bank instructions preview before submit -->
                <div v-if="selectedMethod === 'bank_transfer' && bankDetails.bank_name" class="rounded-lg border border-info/30 bg-info/5 p-3 text-xs">
                  <p class="font-semibold mb-1">You'll get bank details after confirming</p>
                  <p class="ui-text-subtle">Pay to {{ bankDetails.bank_name }} — booking confirms once payment is received.</p>
                </div>
              </template>

              <div class="flex items-center justify-between gap-3 pt-1">
                <span class="text-sm font-medium">{{ priceLabel }}</span>
                <div class="flex gap-2">
                  <UButton variant="ghost" color="neutral" @click="closeAndReset">Cancel</UButton>
                  <UButton
                    color="primary"
                    :loading="submitting"
                    :disabled="!form.customer_name || !form.customer_email || (isPaid && !selectedMethod)"
                    @click="submitBooking"
                  >
                    {{ isPaid ? (selectedMethod === 'stripe' ? 'Pay with card' : selectedMethod === 'qpay' ? 'Generate QR' : 'Continue') : 'Book' }}
                  </UButton>
                </div>
              </div>
            </template>
          </div>
        </template>
      </UModal>
    </template>
  </section>
</template>
